import '../../00group-input/dees-input-multitoggle/dees-input-multitoggle.js';
import { demoFunc } from './dees-harness-composer.demo.js';
import {
  DeesElement,
  html,
  css,
  customElement,
  type TemplateResult,
  property,
  state,
  cssManager,
} from '@design.estate/dees-element';
import { themeDefaultStyles } from '../../00theme.js';
import { harnessControlStyles } from '../harness.styles.js';
import type {
  IHarnessAttachment,
  IHarnessComposerOption,
  IHarnessComposerSendDetail,
  IHarnessComposerSuggestion,
} from '../interfaces.js';
import {
  harnessFileToBase64,
  harnessFormatBytes,
  harnessAttachmentKindFor,
  harnessMaxAttachmentBytes,
  harnessMaxTotalAttachmentBytes,
  harnessMediaTypeForFile,
} from '../harness.helpers.js';
import '../../00group-button/dees-button/dees-button.js';
import { DeesInputDropdown } from '../../00group-input/dees-input-dropdown/dees-input-dropdown.js';
import '../../00group-utility/dees-icon/dees-icon.js';

declare global {
  interface HTMLElementTagNameMap {
    'dees-harness-composer': DeesHarnessComposer;
  }
}

type TDropdownOption = { option: string; key: string };

const toDropdownOptions = (values: string[]): TDropdownOption[] =>
  values.map((value) => ({ option: value, key: value }));

const toLabeledDropdownOptions = (values: IHarnessComposerOption[]): TDropdownOption[] =>
  values.map((value) => ({ option: value.label, key: value.value }));

let nextSuggestionListId = 0;

/**
 * Prompt composer: autogrow textarea, attachments (picker / paste /
 * drag-drop with per-file and total size limits), optional account, model,
 * and reasoning-effort dropdowns. Cmd/Ctrl+Enter sends. While busy, Stop is an
 * additional button and Send stays available for queueing; an empty prompt
 * with queued messages offers "Steer now".
 *
 * Two-way properties: the composer updates `value`, `attachments`, `account`, `model`,
 * `reasoningEffort` and `mode` on itself as the user interacts, then emits the matching
 * change event; a host that mirrors the value back into the property makes an idempotent
 * write. `suggestions` and every option list are read-only inputs. Events: `harness-input`,
 * `harness-attachments-change`, `harness-attachment-error`, `harness-account-change`,
 * `harness-model-change`, `harness-mode-change`, `harness-send`, `harness-steer-input`,
 * `harness-steer` and `harness-abort` (all bubbling + composed).
 */
@customElement('dees-harness-composer')
export class DeesHarnessComposer extends DeesElement {
  public static demo = demoFunc;
  public static demoGroups = ['Agent Chat'];

  @property()
  accessor value: string = '';

  @property()
  accessor placeholder: string = 'Message the agent — drop or paste files to attach…';

  /** Accessible description of the optional `context` slot inside the prompt field. */
  @property()
  accessor contextDescription: string = '';

  /**
   * Docked composers sit flush against their container edge-to-edge (a
   * top border instead of the floating card chrome) — used by
   * dees-harness-chat at the bottom of its conversation column.
   */
  @property({ type: Boolean, reflect: true })
  accessor docked: boolean = false;

  @property({ type: Boolean })
  accessor disabled: boolean = false;

  /**
   * Prevent user mutations while keeping the current draft focusable, selectable,
   * and copyable. This is independent from `disabled` so a host can lock an
   * externally-owned composer without removing access to its contents.
   */
  @property({ type: Boolean })
  accessor inputLocked: boolean = false;

  /**
   * busy = a run is in flight. Sending stays available (hosts queue the
   * messages); Stop renders as an additional button. With an empty prompt
   * and queued messages waiting, the primary action becomes "Steer now"
   * (harness-steer) to force the agent to continue with the queue.
   */
  @property({ type: Boolean })
  accessor busy: boolean = false;

  /** Whether an in-flight run can be stopped by this host. */
  @property({ type: Boolean })
  accessor abortEnabled: boolean = true;

  /** Accessible explanation shown when Stop is present but unavailable. */
  @property()
  accessor abortDisabledReason: string = 'Stopping is unavailable.';

  /** number of messages the host has queued while the run is busy */
  @property({ type: Number })
  accessor queuedCount: number = 0;

  /** Whether the host implements the optional harness-steer action. */
  @property({ type: Boolean })
  accessor steeringEnabled: boolean = true;

  /** Label busy sends as Queue next; the host owns queue admission. */
  @property({ type: Boolean })
  accessor queueingEnabled: boolean = false;

  /** Emit harness-steer-input with the current draft for an exact active-turn steer. */
  @property({ type: Boolean })
  accessor inputSteeringEnabled: boolean = false;

  @property({ attribute: false })
  accessor attachments: IHarnessAttachment[] = [];

  /** Host capability: false disables every attachment entry point and sends text only. */
  @property({ attribute: false })
  accessor attachmentsEnabled: boolean = true;

  @property({ attribute: false })
  accessor suggestions: IHarnessComposerSuggestion[] = [];

  @state()
  private accessor activeSuggestionIndex: number = -1;

  @state()
  private accessor suggestionsDismissed: boolean = false;

  @state()
  private accessor promptFocused: boolean = false;

  private composing: boolean = false;
  private readonly suggestionListId = `dees-harness-composer-suggestions-${++nextSuggestionListId}`;

  @property({ type: Number })
  accessor maxAttachmentBytes: number = harnessMaxAttachmentBytes;

  @property({ type: Number })
  accessor maxTotalAttachmentBytes: number = harnessMaxTotalAttachmentBytes;

  @property({ type: Number })
  accessor maxAttachmentCount: number = Number.POSITIVE_INFINITY;

  @property()
  accessor account: string = '';

  @property({ attribute: false })
  accessor accountOptions: IHarnessComposerOption[] = [];

  @property()
  accessor model: string = '';

  @property({ attribute: false })
  accessor modelOptions: string[] = [];

  @property()
  accessor reasoningEffort: string = '';

  @property({ attribute: false })
  accessor effortOptions: string[] = [];

  /**
   * Agent mode switcher (e.g. Planning / Building). Renders a segmented
   * control when modeOptions is set; emits harness-mode-change.
   */
  @property()
  accessor mode: string = '';

  @property({ attribute: false })
  accessor modeOptions: string[] = [];

  @property({ type: Boolean })
  accessor showModelControls: boolean = true;

  public static styles = [
    themeDefaultStyles,
    cssManager.defaultStyles,
    harnessControlStyles,
    css`
      :host {
        display: block;
        font-family: var(--dees-font-family);
        container-type: inline-size;
      }

      .composerShell {
        position: relative;
        min-width: 0;
      }

      .suggestionList {
        position: absolute;
        z-index: 2;
        inset-inline: 0;
        bottom: calc(100% + var(--dees-spacing-xs));
        box-sizing: border-box;
        max-height: min(240px, 35dvh);
        margin: 0;
        padding: var(--dees-spacing-xs);
        overflow-x: hidden;
        overflow-y: auto;
        overscroll-behavior: contain;
        list-style: none;
        border: 1px solid var(--dees-color-border-subtle);
        border-radius: var(--dees-radius-xl);
        corner-shape: var(--dees-corner-shape);
        background: var(--dees-color-control-bg);
        box-shadow: var(--dees-shadow-up-sm);
      }

      .suggestionOption {
        display: grid;
        align-content: center;
        box-sizing: border-box;
        min-width: 0;
        min-height: 36px;
        padding: var(--dees-spacing-xs) var(--dees-spacing-sm);
        border-radius: var(--dees-radius-lg);
        corner-shape: var(--dees-corner-shape);
        color: var(--dees-color-text-primary);
        cursor: pointer;
        touch-action: manipulation;
      }

      .suggestionOption:hover,
      .suggestionOption.active {
        background: var(--dees-color-fill-secondary);
      }

      .suggestionLabel {
        min-width: 0;
        overflow: hidden;
        font-size: var(--dees-font-control-size, 13px);
        font-weight: 600;
        line-height: 1.35;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .suggestionDescription {
        min-width: 0;
        margin-top: 2px;
        color: var(--dees-color-text-secondary);
        font-size: var(--dees-font-caption1-size, 11px);
        line-height: 1.35;
        overflow-wrap: anywhere;
      }

      .composer {
        display: grid;
        gap: var(--dees-spacing-sm);
        padding: 12px;
        border: 1px solid var(--dees-color-border-subtle);
        border-radius: var(--dees-radius-xl);
        corner-shape: var(--dees-corner-shape);
        background: var(--dees-color-bg-canvas);
      }

      .composer.dragOver {
        border-color: var(--dees-color-accent-primary);
        box-shadow: 0 0 0 3px var(--dees-color-focus-ring);
      }

      :host([docked]) .composer {
        border: 0;
        border-top: 1px solid var(--dees-color-border-subtle);
        border-radius: 0;
        /*
         * The docked bar stays full-bleed — its background and top border belong to the whole
         * column — while its content is inset to the transcript's measure, so the prompt lines up
         * with the messages above it instead of floating as a narrower block.
         */
        padding: 10px max(16px, calc((100% - var(--dees-harness-content-width, 820px)) / 2)) 12px;
      }

      :host([docked]) .composer.dragOver {
        border-top-color: var(--dees-color-accent-primary);
        box-shadow: inset 0 3px 0 0 var(--dees-color-focus-ring);
      }

      .fileInput {
        display: none;
      }

      .promptField {
        display: grid;
        grid-template-columns: minmax(0, 1fr);
        min-width: 0;
        border: 1px solid var(--dees-color-control-border);
        border-radius: var(--dees-radius-xl);
        corner-shape: var(--dees-corner-shape);
        background: var(--dees-color-control-bg);
      }

      .promptField:focus-within {
        border-color: var(--dees-color-accent-primary);
        outline: 2px solid var(--dees-color-focus-ring);
        outline-offset: 1px;
      }

      .promptField:has(textarea:disabled) {
        opacity: 0.55;
      }

      .promptField:has(textarea:read-only:not(:disabled)) {
        background: var(--dees-color-fill-secondary);
      }

      textarea {
        display: block;
        width: 100%;
        min-width: 0;
        min-height: 62px;
        max-height: 40vh;
        resize: none;
        padding: 10px 12px;
        border: 0;
        border-radius: inherit;
        background: transparent;
        color: var(--dees-color-text-primary);
        font-family: inherit;
        font-size: var(--dees-font-control-size, 13px);
        line-height: 1.5;
        outline: none;
        box-sizing: border-box;
      }

      textarea::placeholder {
        color: var(--dees-color-text-muted);
      }

      textarea:focus-visible {
        outline: none;
      }

      textarea:read-only:not(:disabled) {
        color: var(--dees-color-text-primary);
        caret-color: transparent;
        cursor: text;
      }

      .attachmentRow {
        display: flex;
        gap: var(--dees-spacing-xs);
        flex-wrap: wrap;
      }

      .attachmentChip {
        max-width: 100%;
        display: inline-flex;
        align-items: center;
        gap: var(--dees-spacing-xs);
        padding: 3px var(--dees-spacing-xs) 3px var(--dees-spacing-sm);
        border: 1px solid var(--dees-color-border-subtle);
        border-radius: var(--dees-radius-md);
        background: var(--dees-color-bg-tertiary);
        color: var(--dees-color-text-primary);
        font-size: var(--dees-font-caption1-size, 11px);
      }

      .attachmentChip img {
        width: 20px;
        height: 20px;
        border-radius: var(--dees-radius-full);
        object-fit: cover;
      }

      .attachmentChip dees-icon {
        color: var(--dees-color-accent-primary);
      }

      .chipName {
        max-width: 160px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .chipSize {
        color: var(--dees-color-text-secondary);
      }

      .chipRemove {
        display: grid;
        place-items: center;
        width: 24px;
        height: 24px;
        border: 0;
        border-radius: var(--dees-radius-full);
        background: transparent;
        color: var(--dees-color-text-secondary);
        cursor: pointer;
        padding: 0;
      }

      .chipRemove:hover {
        background: var(--dees-color-fill-secondary);
        color: var(--dees-color-text-primary);
      }

      .controlsRow {
        display: flex;
        align-items: center;
        gap: var(--dees-spacing-sm);
      }

      .modeSwitch { flex: 0 0 auto; }

      .modelControls {
        display: flex;
        gap: var(--dees-spacing-xs);
        /* shrinks before the trailing actions do, never grows past its
           content: the row's spacer still pushes send/stop to the edge */
        flex: 0 1 auto;
        min-width: 0;
      }

      .modelControls dees-input-dropdown {
        margin: 0;
        --dees-input-control-height: var(--dees-control-height-md);
        --dees-input-control-bg: var(--dees-color-fill-tertiary);
        --dees-input-control-border: transparent;
      }

      .accountSelect {
        flex: 0 1 180px;
        width: 180px;
        min-width: 120px;
      }

      /* Model identifiers are provider-qualified ("anthropic/claude-opus-5"),
         so the control grows into the free space instead of truncating,
         while still shrinking on narrow hosts. */
      .modelSelect {
        flex: 0 1 280px;
        width: 280px;
        min-width: 150px;
      }

      .effortSelect {
        flex: 0 0 110px;
        width: 110px;
      }

      .spacer {
        flex: 1;
      }

      .queuedStatus {
        flex: 0 0 auto;
        color: var(--dees-color-text-muted);
        font-size: var(--dees-font-caption1-size, 11px);
        font-variant-numeric: tabular-nums;
      }

      @container (max-width: 760px) {
        .controlsRow {
          flex-wrap: wrap;
        }

        .modelControls {
          order: -1;
          flex: 1 0 100%;
          flex-wrap: wrap;
        }

        .accountSelect,
        .modelSelect {
          flex: 1 1 140px;
          width: auto;
          min-width: 0;
        }
      }
    `,
  ];

  public async focusPrompt(): Promise<void> {
    await this.updateComplete;
    this.shadowRoot?.querySelector<HTMLTextAreaElement>('textarea')?.focus();
  }

  /** Opens the native file picker while preserving the caller's user-activation task. */
  public openAttachmentPicker(): boolean {
    if (!this.attachmentsEnabled || this.interactionLocked) return false;
    const input = this.shadowRoot?.querySelector<HTMLInputElement>('.fileInput');
    if (!input) return false;
    input.click();
    return true;
  }

  /** Focuses and opens the model selector when that capability is available. */
  public async openModelSelector(): Promise<boolean> {
    await this.updateComplete;
    if (this.interactionLocked || !this.modelOptions.length || !this.showModelControls) return false;
    const dropdown = this.shadowRoot?.querySelector<DeesInputDropdown>('.modelSelect');
    if (!(dropdown instanceof DeesInputDropdown) || dropdown.disabled) return false;
    dropdown.focus();
    if (!dropdown.isOpened) await dropdown.toggleSelectionBox();
    return true;
  }

  public clear(): void {
    this.value = '';
    this.resetSuggestionInteraction();
    this.emitInput('');
    // Height is resized in updated(); measuring here would read the textarea before it rerenders.
  }

  public willUpdate(changedProperties: Map<PropertyKey, unknown>): void {
    super.willUpdate(changedProperties);
    if (changedProperties.has('suggestions')) this.resetSuggestionInteraction();
  }

  public updated(changedProperties: Map<PropertyKey, unknown>): void {
    super.updated(changedProperties);
    if (['attachmentsEnabled', 'disabled', 'inputLocked'].some(key => changedProperties.has(key) && changedProperties.get(key) !== undefined)) {
      this.attachmentGeneration++;
      if (!this.attachmentsEnabled || this.interactionLocked) this.handleDragLeave();
    }
    // The grown height is an inline style, so it has to be recomputed for every value change
    // regardless of origin. A host that clears its bound value after send would otherwise leave
    // the composer stuck at the height of the message it just sent.
    if (changedProperties.has('value')) this.autogrow();
    this.syncTextareaSuggestionAria();
  }

  public render(): TemplateResult {
    return html`
      <div class="composerShell">
        ${this.suggestionsVisible ? this.renderSuggestions() : ''}
        <div
          class="composer"
          @dragover=${this.handleDragOver}
          @dragleave=${this.handleDragLeave}
          @drop=${this.handleDrop}
        >
        ${this.attachmentsEnabled ? html`<input
          class="fileInput"
          type="file"
          multiple
          ?disabled=${this.interactionLocked}
          @change=${this.handleFileInputChange}
        />` : ''}
        ${this.attachmentsEnabled && this.attachments.length
          ? html`
              <div class="attachmentRow">
                ${this.attachments.map((attachment) => this.renderChip(attachment))}
              </div>
            `
          : ''}
        <div class="promptField" @click=${this.handlePromptFieldClick}>
          <slot name="context"></slot>
          <textarea
            .value=${this.value}
            aria-label="Message"
            aria-description=${this.contextDescription}
            placeholder=${!this.attachmentsEnabled && this.placeholder === 'Message the agent — drop or paste files to attach…' ? 'Message the agent…' : this.placeholder}
            ?disabled=${this.disabled}
            ?readonly=${this.inputLocked}
            rows="1"
            @input=${this.handleTextInput}
            @keydown=${this.handleKeydown}
            @focus=${this.handlePromptFocus}
            @blur=${this.handlePromptBlur}
            @compositionstart=${this.handleCompositionStart}
            @compositionend=${this.handleCompositionEnd}
            @paste=${this.handlePaste}
          ></textarea>
        </div>
        <div class="controlsRow">
          ${this.attachmentsEnabled ? html`<dees-button
            class="attachButton"
            size="sm"
            type="ghost"
            title="Attach files"
            ?disabled=${this.interactionLocked}
            @clicked=${() => {
              this.openAttachmentPicker();
            }}
          >
            <dees-icon icon="lucide:Paperclip" iconSize="14"></dees-icon>
          </dees-button>` : ''}
          ${this.modeOptions.length
            ? html`
                <dees-input-multitoggle class="modeSwitch" label="Agent mode" .labelPosition=${'none'}
                  .options=${this.modeOptions} .selectedOption=${this.mode} .disabled=${this.interactionLocked}
                  @input=${(event: Event) => event.stopPropagation()}
                  @change=${(event: CustomEvent<{ value: string }>) => {
                    event.stopPropagation();
                    if (this.interactionLocked) return;
                    this.mode = event.detail.value;
                    this.emit('harness-mode-change', { mode: this.mode });
                  }}></dees-input-multitoggle>
              `
            : ''}
          ${this.showModelControls && (
            this.accountOptions.length || this.modelOptions.length || this.effortOptions.length
          )
            ? html`
                <div class="modelControls">
                  ${this.accountOptions.length
                    ? html`
                        <dees-input-dropdown
                          class="accountSelect"
                          .options=${toLabeledDropdownOptions(this.accountOptions)}
                          .selectedOption=${this.account
                            ? toLabeledDropdownOptions(this.accountOptions)
                                .find((option) => option.key === this.account) ?? null
                            : null}
                          .disabled=${this.interactionLocked}
                          @selectedOption=${(event: CustomEvent<TDropdownOption>) => {
                            if (this.interactionLocked) return;
                            this.account = event.detail?.key ?? this.account;
                            this.emit('harness-account-change', { account: this.account });
                          }}
                        ></dees-input-dropdown>
                      `
                    : ''}
                  ${this.modelOptions.length
                    ? html`
                        <dees-input-dropdown
                          class="modelSelect"
                          .options=${toDropdownOptions(this.modelOptions)}
                          .selectedOption=${this.model ? { option: this.model, key: this.model } : null}
                          .disabled=${this.interactionLocked}
                          @selectedOption=${(event: CustomEvent<TDropdownOption>) => {
                            if (this.interactionLocked) return;
                            this.model = event.detail?.key ?? this.model;
                            this.emitModelChange();
                          }}
                        ></dees-input-dropdown>
                      `
                    : ''}
                  ${this.effortOptions.length
                    ? html`
                        <dees-input-dropdown
                          class="effortSelect"
                          .options=${toDropdownOptions(this.effortOptions)}
                          .selectedOption=${this.reasoningEffort
                            ? { option: this.reasoningEffort, key: this.reasoningEffort }
                            : null}
                          .disabled=${this.interactionLocked}
                          @selectedOption=${(event: CustomEvent<TDropdownOption>) => {
                            if (this.interactionLocked) return;
                            this.reasoningEffort = event.detail?.key ?? this.reasoningEffort;
                            this.emitModelChange();
                          }}
                        ></dees-input-dropdown>
                      `
                    : ''}
                </div>
              `
            : ''}
          <span class="spacer"></span>
          ${this.busy && this.queuedCount > 0
            ? html`<span class="queuedStatus" aria-live="polite">${this.queuedCount} queued</span>`
            : ''}
          ${this.busy
            ? html`
                <dees-button
                  type="destructive"
                  size="sm"
                  title=${this.abortEnabled ? 'Stop current run' : this.abortDisabledReason}
                  aria-label=${this.abortEnabled ? 'Stop current run' : `Stop current run. ${this.abortDisabledReason}`}
                  ?disabled=${!this.abortEnabled}
                  @clicked=${this.handleAbort}
                >
                  Stop
                </dees-button>
              `
            : ''}
          ${this.busy && this.inputSteeringEnabled
            ? html`<dees-button type="default" size="sm"
                ?disabled=${this.interactionLocked || !this.value.trim()}
                @clicked=${() => this.send('harness-steer-input')}>
                Steer now
              </dees-button>`
            : ''}
          ${this.busy && !this.inputSteeringEnabled && this.steeringEnabled && !this.value.trim() && this.queuedCount > 0
            ? html`
                <dees-button type="accent" size="sm" ?disabled=${this.interactionLocked}
                  @clicked=${() => {
                    if (!this.interactionLocked) this.emit('harness-steer', { queuedCount: this.queuedCount });
                  }}>
                  Steer now
                </dees-button>
              `
            : html`
                <dees-button
                  type="accent"
                  size="sm"
                  ?disabled=${this.interactionLocked || (this.busy && !this.value.trim())}
                  @clicked=${() => this.send()}
                >
                  ${this.busy && this.queueingEnabled ? 'Queue next' : 'Send'}
                </dees-button>
              `}
          </div>
        </div>
      </div>
    `;
  }

  private handlePromptFieldClick(event: MouseEvent): void {
    // Context text is part of the field; preserve native interactions in slotted controls.
    for (const target of event.composedPath()) {
      if (target === event.currentTarget) break;
      if (target instanceof Element
        && target.matches('a[href], button, input, textarea, select, label, [tabindex], [contenteditable]')) return;
    }
    if (!this.disabled) this.shadowRoot?.querySelector<HTMLTextAreaElement>('textarea')?.focus();
  }

  private renderSuggestions(): TemplateResult {
    return html`
      <ul
        id=${this.suggestionListId}
        class="suggestionList"
        role="listbox"
        aria-label="Suggestions"
        @pointerdown=${this.handleSuggestionPointerDown}
      >
        ${this.suggestions.map((suggestion, index) => html`
          <li
            id=${this.suggestionOptionId(index)}
            class=${`suggestionOption ${index === this.activeSuggestionIndex ? 'active' : ''}`}
            role="option"
            aria-selected=${String(index === this.activeSuggestionIndex)}
            @pointerenter=${() => {
              this.activeSuggestionIndex = index;
            }}
            @click=${() => void this.selectSuggestion(index)}
          >
            <span class="suggestionLabel">${suggestion.label}</span>
            ${suggestion.description
              ? html`<span class="suggestionDescription">${suggestion.description}</span>`
              : ''}
          </li>
        `)}
      </ul>
    `;
  }

  private renderChip(attachment: IHarnessAttachment): TemplateResult {
    const imageUrl = attachment.kind === 'image' && attachment.dataBase64
      ? `data:${attachment.mediaType};base64,${attachment.dataBase64}`
      : '';
    return html`
      <span class="attachmentChip">
        ${imageUrl
          ? html`<img src=${imageUrl} alt=${attachment.name} />`
          : html`<dees-icon icon="lucide:FileText" iconSize="13"></dees-icon>`}
        <span class="chipName" title=${attachment.name}>${attachment.name}</span>
        <span class="chipSize">${harnessFormatBytes(attachment.size)}</span>
        <button
          class="chipRemove"
          type="button"
          title="Remove attachment"
          ?disabled=${this.interactionLocked}
          @click=${() => {
            if (this.interactionLocked) return;
            this.emitAttachments(this.attachments.filter((candidate) => candidate.id !== attachment.id));
          }}
        >
          <dees-icon icon="lucide:X" iconSize="11"></dees-icon>
        </button>
      </span>
    `;
  }

  // ------------------------------------------------------------------ input

  private handleTextInput = (event: Event): void => {
    if (this.interactionLocked) {
      (event.currentTarget as HTMLTextAreaElement).value = this.value;
      return;
    }
    this.value = (event.currentTarget as HTMLTextAreaElement).value;
    this.resetSuggestionInteraction();
    this.emitInput(this.value);
    // updated() grows the textarea for every value change, so typing needs no separate call.
  };

  private handleKeydown = (event: KeyboardEvent): void => {
    if (this.interactionLocked) return;
    if (event.isComposing || this.composing) return;
    if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      this.send();
      return;
    }
    if (this.suggestionsVisible) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.suggestionsDismissed = true;
        return;
      }
      if (!event.altKey && !event.ctrlKey && !event.metaKey && !event.shiftKey) {
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          this.moveActiveSuggestion(1);
          return;
        }
        if (event.key === 'ArrowUp') {
          event.preventDefault();
          this.moveActiveSuggestion(-1);
          return;
        }
        if (event.key === 'Enter') {
          event.preventDefault();
          void this.selectSuggestion(this.activeSuggestionIndex);
          return;
        }
      }
    }
    // Shift+Tab cycles the agent mode without leaving the prompt
    if (event.key === 'Tab' && event.shiftKey && this.modeOptions.length) {
      event.preventDefault();
      const currentIndex = this.modeOptions.indexOf(this.mode);
      const nextMode = this.modeOptions[(currentIndex + 1) % this.modeOptions.length];
      this.mode = nextMode;
      this.emit('harness-mode-change', { mode: nextMode });
    }
  };

  private handleCompositionStart = (): void => {
    this.composing = true;
  };

  private handleCompositionEnd = (): void => {
    this.composing = false;
  };

  private handlePromptFocus = (): void => {
    this.promptFocused = true;
  };

  private handlePromptBlur = (): void => {
    this.promptFocused = false;
  };

  private handleSuggestionPointerDown = (event: PointerEvent): void => {
    event.preventDefault();
  };

  private resetSuggestionInteraction(): void {
    this.activeSuggestionIndex = this.suggestions.length ? 0 : -1;
    this.suggestionsDismissed = false;
  }

  private get suggestionsVisible(): boolean {
    return this.promptFocused
      && !this.interactionLocked
      && !this.suggestionsDismissed
      && this.suggestions.length > 0;
  }

  private suggestionOptionId(indexArg: number): string {
    return `${this.suggestionListId}-option-${indexArg}`;
  }

  private syncTextareaSuggestionAria(): void {
    const textarea = this.shadowRoot?.querySelector<HTMLTextAreaElement>('textarea');
    if (!textarea) return;
    const suggestionsAvailable = !this.interactionLocked && this.suggestions.length > 0;
    if (suggestionsAvailable) {
      textarea.setAttribute('aria-autocomplete', 'list');
      textarea.setAttribute('aria-haspopup', 'listbox');
    } else {
      textarea.removeAttribute('aria-autocomplete');
      textarea.removeAttribute('aria-haspopup');
    }
    if (this.suggestionsVisible) {
      textarea.setAttribute('aria-controls', this.suggestionListId);
    } else {
      textarea.removeAttribute('aria-controls');
    }
    if (
      this.suggestionsVisible
      && this.activeSuggestionIndex >= 0
      && this.activeSuggestionIndex < this.suggestions.length
    ) {
      textarea.setAttribute(
        'aria-activedescendant',
        this.suggestionOptionId(this.activeSuggestionIndex),
      );
    } else {
      textarea.removeAttribute('aria-activedescendant');
    }
  }

  private moveActiveSuggestion(offsetArg: -1 | 1): void {
    if (!this.suggestions.length) return;
    this.activeSuggestionIndex = this.activeSuggestionIndex < 0
      ? (offsetArg === 1 ? 0 : this.suggestions.length - 1)
      : (this.activeSuggestionIndex + offsetArg + this.suggestions.length) % this.suggestions.length;
    void this.scrollActiveSuggestionIntoView();
  }

  private async scrollActiveSuggestionIntoView(): Promise<void> {
    await this.updateComplete;
    const list = this.shadowRoot?.getElementById(this.suggestionListId);
    const option = this.shadowRoot?.getElementById(
      this.suggestionOptionId(this.activeSuggestionIndex),
    );
    if (!list || !option) return;
    const optionTop = option.offsetTop;
    const optionBottom = optionTop + option.offsetHeight;
    if (optionTop < list.scrollTop) {
      list.scrollTop = optionTop;
    } else if (optionBottom > list.scrollTop + list.clientHeight) {
      list.scrollTop = optionBottom - list.clientHeight;
    }
  }

  private async selectSuggestion(indexArg: number): Promise<void> {
    if (this.interactionLocked) return;
    const suggestion = this.suggestions[indexArg];
    if (!suggestion) return;
    this.value = suggestion.value;
    this.suggestionsDismissed = true;
    this.emitInput(this.value);
    await this.updateComplete;
    const textarea = this.shadowRoot?.querySelector<HTMLTextAreaElement>('textarea');
    if (!textarea) return;
    textarea.focus();
    textarea.setSelectionRange(this.value.length, this.value.length);
    this.autogrow();
  }

  private autogrow(): void {
    const textarea = this.shadowRoot?.querySelector<HTMLTextAreaElement>('textarea');
    if (!textarea) return;
    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea.scrollHeight, window.innerHeight * 0.4)}px`;
  }

  // ------------------------------------------------------------ attachments

  private handleFileInputChange = (event: Event): void => {
    const input = event.currentTarget as HTMLInputElement;
    if (!this.interactionLocked) void this.addFiles(input.files);
    input.value = '';
  };

  private handlePaste = (event: ClipboardEvent): void => {
    if (!this.attachmentsEnabled || this.interactionLocked) return;
    const files = Array.from(event.clipboardData?.files ?? []);
    if (!files.length) return;
    event.preventDefault();
    void this.addFiles(files);
  };

  private handleDragOver = (event: DragEvent): void => {
    if (!event.dataTransfer?.types.includes('Files')) return;
    event.preventDefault();
    if (!this.attachmentsEnabled || this.interactionLocked) {
      event.dataTransfer.dropEffect = 'none';
      return;
    }
    this.shadowRoot?.querySelector('.composer')?.classList.add('dragOver');
  };

  private handleDragLeave = (): void => {
    this.shadowRoot?.querySelector('.composer')?.classList.remove('dragOver');
  };

  private handleDrop = (event: DragEvent): void => {
    this.shadowRoot?.querySelector('.composer')?.classList.remove('dragOver');
    const files = Array.from(event.dataTransfer?.files ?? []);
    if (!files.length) return;
    event.preventDefault();
    if (!this.attachmentsEnabled || this.interactionLocked) {
      if (event.dataTransfer) event.dataTransfer.dropEffect = 'none';
      return;
    }
    void this.addFiles(files);
  };

  private attachmentAdditionTail = Promise.resolve();
  private attachmentGeneration = 0;

  public addFiles(fileList: FileList | File[] | null): Promise<void> {
    const files = Array.from(fileList ?? []);
    if (!files.length || !this.attachmentsEnabled || this.interactionLocked) return Promise.resolve();
    const generation = this.attachmentGeneration;
    const addition = this.attachmentAdditionTail.then(() => this.addFilesSequential(files, generation));
    this.attachmentAdditionTail = addition.catch(() => undefined);
    return addition;
  }

  private async addFilesSequential(files: File[], generation: number): Promise<void> {
    const allowed = () => this.attachmentsEnabled && !this.interactionLocked && generation === this.attachmentGeneration;
    if (!allowed()) return;
    const added: IHarnessAttachment[] = [];
    let totalBytes = this.attachments.reduce((sum, attachment) => sum + attachment.size, 0);
    for (const file of files) {
      if (!allowed()) return;
      if (this.attachments.length + added.length >= this.maxAttachmentCount) {
        this.emit('harness-attachment-error', {
          message: `A maximum of ${this.maxAttachmentCount} attachments is allowed.`,
        });
        break;
      }
      const mediaType = harnessMediaTypeForFile(file);
      if (file.size > this.maxAttachmentBytes) {
        this.emit('harness-attachment-error', {
          message: `${file.name || 'file'} is too large. Max per attachment is ${harnessFormatBytes(this.maxAttachmentBytes)}.`,
        });
        continue;
      }
      if (totalBytes + file.size > this.maxTotalAttachmentBytes) {
        this.emit('harness-attachment-error', {
          message: `Attachments exceed the ${harnessFormatBytes(this.maxTotalAttachmentBytes)} total limit.`,
        });
        break;
      }
      try {
        const dataBase64 = await harnessFileToBase64(file);
        if (!allowed()) return;
        const kind = harnessAttachmentKindFor(mediaType);
        added.push({
          id: globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`,
          name: file.name || (kind === 'image' ? `pasted-image-${Date.now()}` : `attachment-${Date.now()}`),
          mediaType,
          size: file.size,
          kind,
          dataBase64,
        });
        totalBytes += file.size;
      } catch (error) {
        if (!allowed()) return;
        this.emit('harness-attachment-error', {
          message: error instanceof Error ? error.message : String(error),
        });
      }
    }
    if (added.length && allowed()) {
      this.emitAttachments([...this.attachments, ...added]);
    }
  }

  // ------------------------------------------------------------------ emits

  private send(eventNameArg: 'harness-send' | 'harness-steer-input' = 'harness-send'): void {
    if (this.interactionLocked) return;
    if (eventNameArg === 'harness-steer-input'
      && (!this.busy || !this.inputSteeringEnabled || !this.value.trim())) return;
    const detail: IHarnessComposerSendDetail = {
      text: this.value,
      attachments: this.attachmentsEnabled ? this.attachments : [],
      account: this.account || undefined,
      model: this.model || undefined,
      reasoningEffort: this.reasoningEffort || undefined,
    };
    this.emit(eventNameArg, detail);
  }

  private emitInput(value: string): void {
    this.emit('harness-input', { value });
  }

  private emitAttachments(attachments: IHarnessAttachment[]): void {
    this.attachments = attachments;
    this.emit('harness-attachments-change', { attachments });
  }

  private emitModelChange(): void {
    this.emit('harness-model-change', { model: this.model, reasoningEffort: this.reasoningEffort });
  }

  private handleAbort = (): void => {
    if (!this.busy || !this.abortEnabled) return;
    this.emit('harness-abort', {});
  };

  private get interactionLocked(): boolean {
    return this.disabled || this.inputLocked;
  }

  private emit(name: string, detail: Record<string, unknown> | IHarnessComposerSendDetail): void {
    this.dispatchEvent(new CustomEvent(name, { detail, bubbles: true, composed: true }));
  }
}
